import React, { useState } from 'react'
import { T, useT } from '@transifex/react'
import {
  Button,
  Col,
  Dropdown,
  DropdownProps,
  Input,
  Row,
  Space,
} from 'src/antd'
import styled from 'styled-components'

import Icon, {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
  IconComponentProps,
} from 'src/icons/antd-compat'
import { FilterMenuButton } from 'src/new-components/filter-menu-button'
import { Typography } from 'src/storybook/components'
import { WHTooltip } from 'src/storybook/components/Tooltip'
import { blue600, gray600, red500 } from 'src/styles/color-theme'
import { asyncAlertAction } from 'src/utils/asyncModalAction'

import { TSavedFiltersData } from '../api'

export const DEFAULT_FILTER_KEY = 'system_default_filter'

const StyledStartOutline = styled(StarOutlined)`
  color: ${gray600};

  &:hover {
    color: ${blue600};
  }
`

const RenameIcon = (props: Omit<IconComponentProps, 'ref'>) => (
  <Icon
    {...props}
    component={() => (
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.6799 1.4632H12.0211C12.0562 1.4632 12.0856 1.43495 12.0856 1.39879V0.397659C12.0856 0.36263 12.0573 0.333252 12.0211 0.333252H8.08103C8.046 0.333252 8.01662 0.361501 8.01662 0.397659V1.39879C8.01662 1.43382 8.04487 1.4632 8.08103 1.4632H9.42227C9.4573 1.4632 9.48668 1.49144 9.48668 1.5276V12.4722C9.48668 12.5073 9.45843 12.5366 9.42227 12.5366H8.08103C8.046 12.5366 8.01662 12.5649 8.01662 12.601V13.6033C8.01662 13.6383 8.04487 13.6677 8.08103 13.6677H12.0211C12.0562 13.6677 12.0856 13.6395 12.0856 13.6033V12.601C12.0856 12.566 12.0573 12.5366 12.0211 12.5366H10.6799C10.6449 12.5366 10.6155 12.5084 10.6155 12.4722V1.5276C10.6155 1.49257 10.6437 1.4632 10.6799 1.4632Z"
          fill="#262626"
        />
        <path
          d="M8.96917 9.48579H1.53866C1.49685 9.48579 1.46295 9.4519 1.46295 9.41009V4.58975C1.46295 4.54794 1.49685 4.51404 1.53866 4.51404H8.96917C9.00419 4.51404 9.03357 4.48579 9.03357 4.44964V3.44738C9.03357 3.41235 9.00532 3.38297 8.96917 3.38297H1.53866C0.873121 3.38297 0.333008 3.92308 0.333008 4.58862V9.40896C0.333008 10.0745 0.873121 10.6146 1.53866 10.6146H8.96917C9.00419 10.6146 9.03357 10.5864 9.03357 10.5502V9.54794C9.03357 9.51291 9.00532 9.48354 8.96917 9.48354V9.48579Z"
          fill="#262626"
        />
        <path
          d="M12.4607 3.3841H11.1319C11.0969 3.3841 11.0675 3.41235 11.0675 3.44851V4.45077C11.0675 4.48579 11.0957 4.51517 11.1319 4.51517H12.4607C12.5025 4.51517 12.5364 4.54907 12.5364 4.59088V9.41122C12.5364 9.45303 12.5025 9.48692 12.4607 9.48692H11.1319C11.0969 9.48692 11.0675 9.51517 11.0675 9.55133V10.5536C11.0675 10.5886 11.0957 10.618 11.1319 10.618H12.4607C13.1262 10.618 13.6663 10.0779 13.6663 9.41235V4.58975C13.6663 3.92421 13.1262 3.3841 12.4607 3.3841Z"
          fill="#262626"
        />
      </svg>
    )}
  />
)

type TSavedFilterOptionProps = {
  isDefault: boolean
  setDefault: (id: string, value: boolean) => void
  id: string
  name: string
  setOpen: (val: boolean) => void
  onDelete: (id: string, name: string) => void
  onRename: (id: string, name: string) => void
}

const SavedFilterOption = ({
  isDefault,
  setDefault,
  id,
  name,
  setOpen,
  onDelete,
  onRename,
}: TSavedFilterOptionProps) => {
  const [isRenaming, setRenaming] = useState(false)
  const [value, setValue] = useState(name)

  const handleRename = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    if (value) {
      onRename(id, value)
    } else {
      setValue(name)
    }
    setRenaming(false)
  }

  return (
    <Row justify="space-between" gutter={8}>
      <Col xs={16}>
        <Row gutter={8}>
          <Col xs={3}>
            {isDefault ? (
              <StarFilled
                onClick={(e) => {
                  e.stopPropagation()
                  setDefault(id, false)
                }}
                style={{ color: blue600 }}
              />
            ) : (
              <StyledStartOutline
                onClick={(e) => {
                  e.stopPropagation()
                  setDefault(id, true)
                }}
              />
            )}
          </Col>
          <Col xs={21}>
            {!isRenaming ? (
              <Typography ellipsis style={{ userSelect: 'none' }}>
                {value}
              </Typography>
            ) : (
              <Input
                type="text"
                size="small"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={(e) => {
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              />
            )}
          </Col>
        </Row>
      </Col>
      {isRenaming && (
        <Col xs={8}>
          <Space size={8} style={{ width: '100%', paddingLeft: 6 }}>
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined style={{ fontSize: 14 }} />}
              onClick={handleRename}
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              style={{ color: red500, fontSize: 14 }}
              onClick={(e) => {
                e.stopPropagation()
                setRenaming(false)
                setValue(name)
              }}
            />
          </Space>
        </Col>
      )}
      {!isRenaming && id !== DEFAULT_FILTER_KEY && (
        <Col xs={8}>
          <Space size={8} style={{ width: '100%', justifyContent: 'flex-end' }}>
            <WHTooltip title={<T _str="Rename" />}>
              <Button
                type="text"
                size="small"
                icon={<RenameIcon />}
                onClick={(e) => {
                  e.stopPropagation()
                  setRenaming(true)
                }}
              />
            </WHTooltip>
            <WHTooltip title={<T _str="Delete" />}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(false)
                  onDelete(id, name)
                }}
              />
            </WHTooltip>
          </Space>
        </Col>
      )}
    </Row>
  )
}

const DropdownWithOverlayClassname = ({
  className,
  ...props
}: DropdownProps & { className?: string }) => (
  <Dropdown {...props} overlayClassName={className} />
)

const StyledDropdown = styled(DropdownWithOverlayClassname)`
  width: 320px;

  /* Both renderings: the Radix group is the list antd splits in two, and
     overlayClassName lands on the panel itself rather than on a wrapper. */
  .ant-dropdown-menu .ant-dropdown-menu-item-group-list {
    margin: 0;
  }

  &[data-slot='dropdown-menu-content'] [data-slot='dropdown-menu-group'] {
    margin: 0;
  }
`

type SavedFilterButtonProps = {
  filtersData: TSavedFiltersData[]
  selectedSavedFilter?: string
  hasDefaultFilter: boolean
  setSelectedSavedFilter: (val: string) => void
  onRename: (id: string, name: string) => void
  onSetDefault: (id: string, value: boolean) => void
  onDelete: (id: string) => void
}

const SavedFilterButton = ({
  filtersData,
  selectedSavedFilter,
  setSelectedSavedFilter,
  onRename,
  onSetDefault,
  onDelete,
  hasDefaultFilter,
}: SavedFilterButtonProps) => {
  const [open, setOpen] = useState(false)
  const t = useT()

  const savedFilterSelectedValue = filtersData.find(
    (f) => f.id === selectedSavedFilter
  )

  const handleDeleteWithConfirm = (id: string, name: string) => {
    asyncAlertAction({
      modalProps: {
        title: <T _str="Delete filter" />,
      },
      confirmProps: {
        submitButtonProps: {
          children: <T _str="Delete filter" />,
          icon: null,
        },
        cancelButtonProps: {
          icon: null,
        },
        title: <T _str="You are going to remove filter: {name}" name={name} />,
        variant: 'warning',
      },
      asyncCall: async ({ closeModal }) => {
        onDelete(id)
        closeModal()
      },
    })
  }

  return (
    <StyledDropdown
      open={open}
      onOpenChange={(o) => setOpen(o)}
      destroyPopupOnHide
      menu={{
        items: [
          {
            key: '1',
            type: 'group',
            label: <T _str="Saved filters" />,
            children: [
              {
                id: DEFAULT_FILTER_KEY,
                isDefault: !hasDefaultFilter,
                name: t('Custom'),
              },
              ...filtersData,
            ].map(({ isDefault, name, id }) => ({
              label: (
                <SavedFilterOption
                  id={id}
                  isDefault={isDefault}
                  setDefault={onSetDefault}
                  name={name}
                  setOpen={setOpen}
                  onDelete={handleDeleteWithConfirm}
                  onRename={onRename}
                />
              ),
              key: id,
            })),
          },
        ],
        selectable: true,
        selectedKeys: selectedSavedFilter
          ? [selectedSavedFilter]
          : [DEFAULT_FILTER_KEY],
        onClick: (e) => {
          setOpen(false)
          setSelectedSavedFilter(e.key)
        },
      }}
      trigger={['click']}
    >
      <FilterMenuButton withCaretIcon icon={<StarOutlined />}>
        {savedFilterSelectedValue?.name && (
          <Typography>{savedFilterSelectedValue.name}</Typography>
        )}
      </FilterMenuButton>
    </StyledDropdown>
  )
}

export default SavedFilterButton
